import { createSelector } from '@reduxjs/toolkit';
import { DriverIcon, KIcon } from '@karnott/icons';
import { DEVICE_CONSTANTS } from '../constants/deviceConstants';
import {
  BeaconDriverHistory,
  BeaconEquipmentHistory,
  selectBeaconDriverHistoryById,
  selectBeaconEquipmentHistoryById,
  selectBeaconsById,
  selectBeaconsIds,
} from '../slices/beacons';
import { selectClustersById } from '../slices/clusters';
import { selectEquipmentModelsById, selectEquipmentsById } from '../slices/equipments';
import { selectSearchFilter, selectSelectedClusters } from '../slices/ui';
import { selectUsersByID } from '../slices/users';
import { clusterContains } from '../utils';
import { beaconsContains } from '../utils/devices';
import { getEquipmentIcon } from '../utils/equipments';
import { getUserName } from '../utils/user';

export const selectVisibleBeacons = createSelector(
  [selectSelectedClusters, selectSearchFilter, selectBeaconsIds, selectBeaconsById, selectClustersById],
  (activeClusters, filter, beaconIds, beaconsById, clustersById) =>
    beaconIds
      .map((id) => beaconsById[id]!)
      .filter((b) => b.status !== DEVICE_CONSTANTS.STATUS.DELETED)
      .filter((b) => !activeClusters?.length || activeClusters.includes(b.clusterId))
      .map((beacon) => {
        const cluster = clustersById[beacon.clusterId];
        return {
          ...beacon,
          cluster: cluster,
          deviceType:
            beacon.type === DEVICE_CONSTANTS.BEACON_TYPE.EQUIPMENT
              ? DEVICE_CONSTANTS.TYPE.BEACON
              : DEVICE_CONSTANTS.TYPE.DRIVER_BEACON,
          hidden: !beaconsContains(beacon, filter) && !clusterContains(cluster, filter),
        };
      }),
);

export const selectVisibleEquipmentBeacons = createSelector([selectVisibleBeacons], (beacons) =>
  beacons.filter((b) => b.type === DEVICE_CONSTANTS.BEACON_TYPE.EQUIPMENT),
);

export const selectVisibleDriverBeacons = createSelector([selectVisibleBeacons], (beacons) =>
  beacons.filter((b) => b.type === DEVICE_CONSTANTS.BEACON_TYPE.DRIVER),
);

export const selectDriverBeaconsByClusterId = createSelector([selectVisibleDriverBeacons], (beacons) =>
  beacons.reduce<Record<string, typeof beacons>>((beaconsByClusterId, b) => {
    beaconsByClusterId[b.clusterId] = beaconsByClusterId[b.clusterId] || [];
    beaconsByClusterId[b.clusterId]!.push(b);
    return beaconsByClusterId;
  }, {}),
);

export const selectEquipmentBeaconHistory = createSelector(
  [selectBeaconEquipmentHistoryById, selectEquipmentsById, selectEquipmentModelsById],
  (beaconEquipmentHistoryById, equipmentById, equipmentModelById) => {
    const updatedBeaconEquipmentHistoryById = Object.keys(beaconEquipmentHistoryById).reduce<
      Record<string, (BeaconEquipmentHistory & { label: string; icon: KIcon })[]>
    >((acc, beaconId) => {
      const history = beaconEquipmentHistoryById[beaconId];
      const updatedHistory = (history || []).map((historyItem) => {
        const equipment = equipmentById[historyItem.equipment_instance_id];
        const equipmentModel = equipment ? equipmentModelById[equipment.equipmentModelId] : undefined;
        return {
          ...historyItem,
          label: equipment?.label || '',
          icon: getEquipmentIcon((equipmentModel && equipmentModel.type) || 'misc'),
        };
      });
      acc[beaconId] = updatedHistory;
      return acc;
    }, {});

    return updatedBeaconEquipmentHistoryById;
  },
);

export const selectDriverBeaconHistory = createSelector(
  [selectBeaconDriverHistoryById, selectUsersByID],
  (beaconDriverHistoryById, drivers) => {
    const updatedBeaconDriverHistoryById = Object.keys(beaconDriverHistoryById).reduce<
      Record<string, (BeaconDriverHistory & { label: string; icon: KIcon })[]>
    >((acc, beaconId) => {
      const history = beaconDriverHistoryById[beaconId];
      const updatedHistory = (history || []).map((historyItem) => {
        const driver = drivers[historyItem.user_id];
        return {
          ...historyItem,
          label: driver ? getUserName(driver) : '',
          icon: DriverIcon,
        };
      });
      acc[beaconId] = updatedHistory;
      return acc;
    }, {});

    return updatedBeaconDriverHistoryById;
  },
);

export const selectHasEquipmentBeacon = createSelector(
  [selectVisibleEquipmentBeacons],
  (beacons) => beacons.length !== 0,
);

export const selectHasDriverBeacon = createSelector([selectVisibleDriverBeacons], (beacons) => beacons.length !== 0);
